import * as Sentry from '@sentry/react'

import { targetingSearchResponseSchema, zipMatchResponseSchema } from './schemas'

// The v4 unified search/zip-match response has no single opaque id spanning
// both platforms (Google uses geo_target_constant_id, Meta uses key) — the
// FE location list (add/remove/radius/qualifier, all keyed by `data.key`, and
// the fb/google merge-on-load in rangeLocationsReducer's initialState) needs
// one stable, platform-agnostic key per real-world location. Deliberately
// NOT platform-id-based (a fb key and a google geo_target_constant_id for
// the "same" city are different ID spaces and can't be merged against each
// other once each platform's saved payload only carries its own id) — uses
// canonical_name (or the name/type/country_code fallback), which is
// reconstructable from what gets persisted into both fb and google range/
// postal-code wrappers, so a reloaded TC's fb and google entries for the
// same place merge back into one row instead of two.
export function locationResultKey(item) {
  return item.canonical_name ?? `${item.name}|${item.type}|${item.country_code ?? ''}`
}

export function mapSearchResult(item) {
  return { ...item, key: locationResultKey(item) }
}

export function mapSearchResults(results = []) {
  return results.map(mapSearchResult)
}

export function mapSearchResponse(data) {
  const parsed = targetingSearchResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = (parsed.success ? parsed.data : data) ?? {}
  return { results: mapSearchResults(source.results) }
}

export function mapZipMatchItem(item) {
  // The raw zip-match payload is just { zip, platform_results } — with no
  // name/type, locationResultKey would return "undefined|undefined|" for
  // every matched zip. A matched item is always a postal code, so fill in
  // name/type from the zip before keying it.
  const normalized = { ...item, name: item.name ?? item.zip, type: item.type ?? 'zip' }
  return { ...normalized, key: locationResultKey(normalized) }
}

export function mapZipMatchResponse(data) {
  const parsed = zipMatchResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = (parsed.success ? parsed.data : data) ?? {}
  return {
    matched: (source.matched ?? []).map(mapZipMatchItem),
    unmatched: source.unmatched ?? [],
  }
}
